import { allFromSnapshot, oneFromSnapshot, } from "@buildwithflux/firestore-compatibility-layer";
import { isDocumentNameValid } from "@buildwithflux/models";
import { guid } from "@buildwithflux/shared";
const NEW_DOCUMENT_CONTENT = `# Project Specification

**Version:** 0.1.0
**Date:** ...
**Author:** ...
**Status:** ...
**Tags:** ...

---

## 1. Executive Summary

*What board we're building and the intended outcome in one paragraph.*
...

## 2. Goals and Non-Goals

*What must be true in this rev, and what is explicitly out of scope.*
...

### 2.1 Goals

...

### 2.2 Non-Goals

...

## 3. System Overview

*High-level description of the system and how the board fits into it.*
...

### 3.1 Block Diagram

*A space for a simple block diagram (power + data paths).*
...

## 4. Requirements

*What the design must do (behaviorally and electrically), independent of implementation details.*
...

### 4.1 Functional Requirements

...

### 4.2 Electrical Requirements

*Power input, rails, currents, interfaces, sensing accuracy, etc.*
...

### 4.3 Environmental and Reliability Requirements

*Operating ranges, expected lifetime, handling constraints, safety/compliance.*
...

## 5. Constraints

*Hard limits that shape the design choices.*
...

### 5.1 Mechanical Constraints

*Size, mounting, connector access, enclosure interactions.*
...

### 5.2 Power and Thermal Constraints

*Power source limits, dissipation ceilings, heat-sensitive components.*
...

### 5.3 Layout / Implementation Constraints

*High-level layout rules (keepouts, grounding intent, routing classes), without detailed PCB guidance.*
...

### 5.4 Cost and Sourcing Constraints

*Target cost range, availability requirements, preferred vendors/alternates policy.*
...

## 6. Assumptions and Dependencies

*What we assume is true, and what the design depends on (parts, standards, external systems).*
...

## 7. Validation Criteria

*What “done” means in terms of checks and evidence (not task lists).*
...

### 7.1 Acceptance Tests

...

### 7.2 Review Checklist

...

## Appendix (Optional)

*Any supporting urls, analysis, datasets, or taxonomy notes that informed this spec.*
...
`;
const NEW_DOCUMENT_CONTENT2 = `# Doc`;
// Helper to derive document name from content
export const deriveDocumentName = (content) => {
    let name = "Untitled Document";
    // 1. Try to find the first H1
    const h1Match = content.match(/^#\s+(.+)$/m);
    if (h1Match && h1Match[1]?.trim()) {
        name = h1Match[1].trim();
    }
    else {
        // 2. Fallback: first non-empty line
        const firstLine = content.split("\n").find((line) => line.trim().length > 0);
        if (firstLine) {
            name = firstLine.trim();
            // Remove leading hashes if present (e.g. ## Subheader)
            name = name.replace(/^#+\s*/, "");
        }
    }
    // 3. Truncate to 150 chars (max allowed by DocumentName)
    if (name.length > 150) {
        name = name.substring(0, 150);
    }
    // 4. Validate against conventions
    if (!isDocumentNameValid(name)) {
        return "Untitled Document";
    }
    return name;
};
export class FirestoreProjectDocRepository {
    db;
    logger;
    constructor(db, logger) {
        this.db = db;
        this.logger = logger;
    }
    async getProjectDocs(documentUid) {
        return allFromSnapshot(await this.db.documentProjectDocsCollection(documentUid).get());
    }
    async getProjectDoc(documentUid, projectDocUid) {
        return oneFromSnapshot(await this.db.documentProjectDocsCollection(documentUid).doc(projectDocUid).get(), () => undefined);
    }
    async createProjectDoc(documentUid, markdownDoc) {
        markdownDoc.created_at = markdownDoc.created_at || Date.now();
        markdownDoc.updated_at = markdownDoc.updated_at || Date.now();
        markdownDoc.uid = markdownDoc.uid || guid();
        if (!markdownDoc.content) {
            const num_docs = (await this.db.documentProjectDocsCollection(documentUid).get()).docs.length;
            markdownDoc.content =
                num_docs === 0 ? NEW_DOCUMENT_CONTENT : NEW_DOCUMENT_CONTENT2 + " " + (num_docs + 1) + "\n\n---\n\n";
        }
        if (!markdownDoc.name) {
            markdownDoc.name = deriveDocumentName(markdownDoc.content);
        }
        await this.db.documentProjectDocsCollection(documentUid).doc(markdownDoc.uid).set(markdownDoc);
        return markdownDoc;
    }
    async updateProjectDoc(documentUid, markdownDoc) {
        if (markdownDoc.content) {
            const name = deriveDocumentName(markdownDoc.content);
            markdownDoc.name = name;
        }
        markdownDoc.updated_at = Date.now();
        await this.db.documentProjectDocsCollection(documentUid).doc(markdownDoc.uid).set(markdownDoc, { merge: true });
    }
    async deleteProjectDoc(documentUid, projectDocUid) {
        await this.db.documentProjectDocsCollection(documentUid).doc(projectDocUid).delete();
    }
    subscribeToProjectDocs(documentUid, onReceive, onDelete, onError) {
        return this.db.documentProjectDocsCollection(documentUid).onSnapshot((snap) => {
            const addedOrChanged = [];
            const deleted = [];
            // eslint-disable-next-line @buildwithflux/no-array-foreach
            snap.docChanges().forEach((change) => {
                const data = change.doc.data();
                // We are casting/assuming data matches summary structure as it's a subset of full doc
                // Ideally we might want to safeParse, but for speed in subscriptions we often trust schema or partial
                // Since IProjectDocSummary is a subset of IProjectDoc, this cast/usage is safe if data is valid
                const summary = data;
                if (change.type === "removed") {
                    deleted.push(summary);
                }
                else {
                    addedOrChanged.push(summary);
                }
            });
            onReceive(addedOrChanged);
            onDelete(deleted);
        }, onError);
    }
    subscribeToProjectDoc(documentUid, projectDocUid, onUpdate, onError) {
        return this.db
            .documentProjectDocsCollection(documentUid)
            .doc(projectDocUid)
            .onSnapshot((snap) => {
            if (!snap.exists) {
                onUpdate(undefined);
                return;
            }
            const data = snap.data();
            onUpdate(data);
        }, onError);
    }
}
//# sourceMappingURL=FirestoreProjectDocRepository.js.map